/** @format */

import { assetWidthCss, Brand, getAssetBlockKey, getQuestionContentBlocks, Question } from "@question-bank/shared";
import { getAssetUrl } from "../api/questions";
import { MathText } from "./MathText";
import { ProxiedImage } from "./ProxiedImage";

interface QuestionContentBlocksProps {
	question: Pick<Question, "contentBlocks" | "en" | "cy">;
	/** Language code to render text blocks in. */
	language: "en" | "cy";
	brand: Brand;
	containerClassName?: string;
	textClassName?: string;
	imageClassName?: string;
	/** Fallback alt text for asset blocks that carry none of their own. */
	imageAlt?: string;
	/** Class for the wrapper around an answer-lines block's ruled lines. */
	answerLinesClassName?: string;
	/** Class for a single ruled answer line. */
	answerLineClassName?: string;
}

/**
 * Renders a question's ordered body blocks (interleaved prose, images and
 * ruled answer lines) for the given language. Text and image blocks fall back
 * to the other language when the requested one is empty, so a question can mix
 * per-language copy and per-language figures.
 */
export const QuestionContentBlocks = ({
	question,
	language,
	brand,
	containerClassName,
	textClassName,
	imageClassName,
	imageAlt = "",
	answerLinesClassName = "flex flex-col gap-2 py-1",
	answerLineClassName = "h-6 border-b border-dashed border-(--public-color-text-lighter,#999)",
}: QuestionContentBlocksProps) => {
	const blocks = getQuestionContentBlocks(question);
	if (blocks.length === 0) {
		return null;
	}

	return (
		<div className={containerClassName}>
			{blocks.map((block, index) => {
				if (block.type === "text") {
					const text = language === "cy" ? (block.cy ?? block.en) : (block.en ?? block.cy);
					if (!text?.trim()) {
						return null;
					}
					return (
						<p key={`block-${index}`} className={textClassName}>
							<MathText text={text} />
						</p>
					);
				}
				if (block.type === "answerLines") {
					if (block.lines <= 0) {
						return null;
					}
					return (
						<div key={`block-${index}`} className={answerLinesClassName}>
							{Array.from({ length: block.lines }).map((_, lineIndex) => (
								<div key={lineIndex} className={answerLineClassName} />
							))}
						</div>
					);
				}
				const assetKey = getAssetBlockKey(block, language);
				if (!assetKey) {
					return null;
				}
				// An authored width is a percentage of the container, so the image
				// stays responsive; without one the block keeps its existing sizing.
				const width = assetWidthCss(block.width);
				return (
					<ProxiedImage
						key={`block-${index}`}
						src={getAssetUrl(brand, assetKey)}
						alt={block.alt ?? imageAlt}
						className={imageClassName}
						style={width ? { width, maxWidth: "100%" } : undefined}
					/>
				);
			})}
		</div>
	);
};
